import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, TextField } from '@mui/material';
import { useState } from 'react';
import { PlaceRef } from 'src/entities/Course.entity';

import { stayMinutesLabel } from './courseMasterUtils';
import PlaceAutocomplete from './PlaceAutocomplete';
import { STOP_COLS } from './StopRow';

const muted = 'text.secondary';
type LoadingTimeField = 'loadingArrivalTime' | 'loadingDepartureTime';
type Props = {
  place: PlaceRef | null;
  arrivalTime: string | null;
  departureTime: string | null;
  invalid: boolean;
  disabled: boolean;
  onPlace: (place: PlaceRef | null) => void;
  onTime: (field: LoadingTimeField, value: string | null) => void;
};

/**
 * 積地の 1 行（log_web_issues#384 12。サンプル A）。配送先の行と同じ列に、積地の印・地点コード・名前・住所・到着・出発・滞在を出す。
 * 場所は「編集」（未設定なら「設定」）でその場に地点の欄を出し、「決定」で確定する（× で空にして決定すると未設定）。時刻はいつでもその場で直せる
 */
export default function LoadingPlaceRow({ place, arrivalTime, departureTime, invalid, disabled, onPlace, onTime }: Props) {
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<PlaceRef | null>(place);
  const startEdit = () => {
    setPending(place);
    setEditing(true);
  };
  const decide = () => {
    if (pending?.id !== place?.id) onPlace(pending);
    setEditing(false);
  };
  const values: Record<LoadingTimeField, string | null> = { loadingArrivalTime: arrivalTime, loadingDepartureTime: departureTime };
  const time = (field: LoadingTimeField, label: string) => (
    <TextField
      key={`${field}-${values[field] ?? ''}`}
      type="time"
      size="small"
      defaultValue={values[field] ?? ''}
      disabled={disabled}
      slotProps={{
        htmlInput: {
          'aria-label': `積地の${label}`,
          'aria-invalid': invalid,
          'aria-describedby': invalid ? 'route-time-error' : undefined,
        },
      }}
      onBlur={(e) => {
        const next = e.target.value || null;
        if (next === values[field]) return;
        onTime(field, next);
        // 捨てられた変更（保存中の重なり）を入力欄に残さない。受け付けた変更は key が変わり作り直される（StopRow と同じ）
        e.target.value = values[field] ?? '';
      }}
    />
  );
  return (
    <Box
      data-testid="loading-row"
      aria-invalid={invalid}
      sx={{
        display: 'grid',
        gridTemplateColumns: STOP_COLS,
        minWidth: 900,
        gap: 1,
        alignItems: 'center',
        px: 1,
        py: 1,
        mb: 0.5,
        borderLeft: 3,
        borderLeftColor: 'grey.400',
        borderRadius: 1,
        bgcolor: invalid ? 'rgba(244, 67, 54, 0.08)' : 'action.hover',
        outline: invalid ? '2px solid' : 'none',
        outlineColor: 'error.main',
      }}
    >
      <span />
      <Box sx={{ fontSize: 12, fontWeight: 700, color: muted }}>積地</Box>
      <Box sx={{ fontSize: 13, color: muted }}>{place?.internalLocationCode ?? '—'}</Box>
      {editing ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <PlaceAutocomplete value={pending} onChange={setPending} label="積地（地点マスタ）" disabled={disabled} />
          </Box>
          <Button size="small" variant="contained" disableElevation onClick={decide} disabled={disabled}>決定</Button>
          <Button size="small" onClick={() => setEditing(false)}>キャンセル</Button>
        </Box>
      ) : (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {place
              ? <Box component="span" sx={{ fontSize: 14, fontWeight: 600 }}>{place.name}</Box>
              : <Box component="span" sx={{ fontSize: 14, color: muted }}>未設定</Box>}
            <Button
              size="small"
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={startEdit}
              disabled={disabled}
              aria-label={place ? '積地を編集' : '積地を設定'}
              sx={{ py: 0, minWidth: 0 }}
            >
              {place ? '編集' : '設定'}
            </Button>
          </Box>
          {place && <Box sx={{ fontSize: 12, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{place.address}</Box>}
        </Box>
      )}
      {time('loadingArrivalTime', '到着')}
      {time('loadingDepartureTime', '出発')}
      <Box sx={{ fontSize: 13, color: muted }}>{stayMinutesLabel(arrivalTime, departureTime)}</Box>
      <span />
    </Box>
  );
}
